import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import './ConfirmationModal.css';

export const ConfirmationModal = ({
  isOpen = false,
  onClose,
  onConfirm,
  onSecondaryConfirm = null, // Optional second action button
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  secondaryConfirmText = '', // Text for second action button
  cancelText = 'Cancel',
  type = 'warning', // 'warning', 'danger', 'info'
  secondaryType = 'danger', // Type for second action button
  hideCancel = false, // Hide cancel button
  className = '',
  ...props
}) => {
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleSecondaryConfirm = () => {
    if (onSecondaryConfirm) {
      onSecondaryConfirm();
    }
    onClose();
  };

  if (!isOpen) return null;

  const baseClass = 'confirmation-modal';
  const combinedClassName = [baseClass, className].filter(Boolean).join(' ');

  return createPortal(
    <div className="confirmation-modal__overlay">
      <div className="scrim" onClick={onClose} />
      <div ref={modalRef} className={combinedClassName}>
        <div className="confirmation-modal__header">
          <h3 className="confirmation-modal__title">
            {title}
          </h3>
          <Button
            type="ghost"
            size="small"
            icon={<Icon name="close" size="small" />}
            onClick={onClose}
            className="confirmation-modal__close-button"
          />
        </div>
        
        <div className="confirmation-modal__content">
          <p className="confirmation-modal__message">
            {message}
          </p>
        </div>

        <div className="confirmation-modal__actions">
          {!hideCancel && (
            <Button
              type="ghost"
              size="medium"
              onClick={onClose}
              className="confirmation-modal__cancel-button"
            >
              {cancelText}
            </Button>
          )}
          {onSecondaryConfirm && secondaryConfirmText && (
            <Button
              type="solid"
              size="medium"
              onClick={handleSecondaryConfirm}
              className={`confirmation-modal__confirm-button confirmation-modal__confirm-button--${secondaryType}`}
            >
              {secondaryConfirmText}
            </Button>
          )}
          <Button
            type={type === 'danger' ? 'solid' : 'solid'}
            size="medium"
            onClick={handleConfirm}
            className={`confirmation-modal__confirm-button confirmation-modal__confirm-button--${type}`}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
